import api, { request } from '../../../api';
import { getObjectByIdFromArray } from '../../../contrib/Utils/getObjectByIdFromArray';
import { IErrorsStore } from '../../errors/store';
import { IActionGroup, IStore } from '../../index';
import { IAdminEventRoundsStore, IEventDetailedRound, IRoundGame, IRoundGroup } from './store';

export type TAddPlayerToGroup = (
  clubId: number,
  eventId: number,
  roundId: number,
  game: Partial<IRoundGame>
) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };

export type TSwapPlayers = (
  clubId: number,
  eventId: number,
  roundId: number,
  enrollentId1: number,
  enrollentId2: number
) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };

export interface IAdminEventRoundsActions extends IActionGroup {
  addPlayerToGroup: TAddPlayerToGroup;
  clear: (
    clubId: number,
    eventId: number,
    roundId: number
  ) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };
  get: (
    clubId: number,
    eventId: number,
    roundId?: number,
    force?: boolean
  ) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };
  removePlayerFromGroup: (
    clubId: number,
    eventId: number,
    roundId: number,
    enrollentId: number
  ) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };
  swapPlayers: TSwapPlayers;
  updateGames: (
    clubId: number,
    eventId: number,
    roundId: number,
    data: any
  ) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };
  setCurrent: (
    round: IEventDetailedRound
  ) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };
  autoFill: (
    clubId: number,
    eventId: number,
    roundId: number
  ) => { adminEventRounds?: IAdminEventRoundsStore; errors?: IErrorsStore };
}

const actions: IActionGroup = {
  addPlayerToGroup: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    game: Partial<IRoundGame>
  ) => {
    const body = game;
    const response = await request.post(
      `${api.admin.events(clubId)}${eventId}/rounds/${roundId}/games/`,
      body
    );

    if (response.error) {
      return { errors: { adminEventRounds: { msg: 'Error Adding player to Group.' } } };
    }

    const newGame: IRoundGame = response;
    const round = state.adminEventRounds[roundId];
    const group = round.groups.find(g => g.id === newGame.group);
    if (group) {
      const newGroup = { ...group, games: [...group.games, newGame] };
      const newGroups = round.groups.map(g => (g.id === newGame.group ? newGroup : g));

      return {
        adminEventRounds: { ...state.adminEventRounds, [roundId]: { ...round, groups: newGroups } }
      };
    }

    return { errors: { events: { msg: 'Error: Group not found.' } } };
  },
  get: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId?: number,
    force?: boolean
  ) => {
    if (roundId && state.adminEventRounds[roundId] && !force) {
      return { adminEventRounds: state.adminEventRounds };
    }

    const response = await request.get(api.admin.eventRounds(clubId, eventId, roundId));

    if (response.error) {
      return { errors: { adminEventRounds: { msg: 'Error retrieving the Event Round Details.' } } };
    }

    const round: IEventDetailedRound = response;

    const adminEventRounds = roundId
      ? { [round.id]: round, current: round }
      : getObjectByIdFromArray(response);

    return { adminEventRounds: { ...state.adminEventRounds, ...adminEventRounds } };
  },
  removePlayerFromGroup: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    enrollentId: number
  ) => {
    const response = await request.delete(
      `${api.admin.events(clubId)}${eventId}/rounds/${roundId}/games/${enrollentId}/`
    );

    if (response.error) {
      return { errors: { adminEventRounds: { msg: 'Error removing player from Group.' } } };
    }

    const round = state.adminEventRounds[roundId];

    const group = (round as IEventDetailedRound).groups.find(
      gr => !!gr.games.find(ga => ga.enrollment === enrollentId)
    );

    if (group) {
      const newGroup = {
        ...group,
        games: group.games.filter((g: IRoundGame) => g.enrollment !== enrollentId)
      };

      const newGroups = (round as IEventDetailedRound).groups.map(g =>
        g.id === newGroup.id ? newGroup : g
      );

      return {
        adminEventRounds: {
          ...state.adminEventRounds,
          [roundId]: {
            ...round,
            groups: newGroups
          }
        }
      };
    }

    return { errors: { adminEventRounds: { msg: 'Error removing player from Group.' } } };
  },
  swapPlayers: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    enrollentId1: number,
    enrollentId2: number
  ) => {
    const body = { enrollment_1: enrollentId1, enrollment_2: enrollentId2 };
    const response = await request.post(
      `${api.admin.events(clubId)}${eventId}/rounds/${roundId}/games/swap/`,
      body
    );

    if (response.error) {
      return { errors: { adminEventRounds: { msg: 'Error Adding player to Group.' } } };
    }

    const round = state.adminEventRounds[roundId];

    // separate games
    const newGame1 = response.find((r: IRoundGame) => r.enrollment === enrollentId1);
    const newGame2 = response.find((r: IRoundGame) => r.enrollment === enrollentId2);
    // find groups if exists
    const group1 = round.groups.find(gr => !!gr.games.find(ga => ga.enrollment === enrollentId1));
    const group2 = round.groups.find(gr => !!gr.games.find(ga => ga.enrollment === enrollentId2));

    let newGroup1: IRoundGroup | undefined;
    let newGroup2: IRoundGroup | undefined;

    if (group1 && group2 && group1.id === group2.id) {
      newGroup1 = {
        ...group1,
        games: group1.games
          .filter((g: IRoundGame) => g.enrollment !== enrollentId1 && g.enrollment !== enrollentId2)
          .concat([newGame1, newGame2])
      };
    } else {
      // swap enrollment1 to enrollment2 from group1 if exists
      newGroup1 = group1 && {
        ...group1,
        games: group1.games.map((g: IRoundGame) => (g.enrollment === enrollentId1 ? newGame2 : g))
      };

      // swap enrollment2 to enrollment1 from group2 if exists
      newGroup2 = group2 && {
        ...group2,
        games:
          group2 &&
          group2.games.map((g: IRoundGame) => (g.enrollment === enrollentId2 ? newGame1 : g))
      };
    }

    const newRound = {
      ...round,
      groups: round.groups.map(g => {
        if (newGroup1 && newGroup1.id === g.id) {
          return newGroup1;
        }
        if (newGroup2 && newGroup2.id === g.id) {
          return newGroup2;
        }
        return g;
      })
    };

    return {
      adminEventRounds: {
        ...state.adminEventRounds,
        [roundId]: newRound
      }
    };
  },
  updateGames: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    instances: Array<Partial<IRoundGame>>
  ) => {
    const response = await request.patch(
      `${api.admin.eventRounds(clubId, eventId, roundId)}games/`,
      { instances }
    );

    if (response.error) {
      return { errors: { groups: { msg: 'Error updating the game' } } };
    }

    // state.adminEventRounds[]
    const newGames = response as IRoundGame[];

    const round = state.adminEventRounds[roundId];
    const groups = round.groups.map(g => {
      const games = g.games.map(game => {
        const newGame = newGames.find(ng => ng.id === game.id);
        return newGame || game;
      });
      return {
        ...g,
        games
      };
    });

    return {
      adminEventRounds: {
        ...state.adminEventRounds,
        [roundId]: {
          ...round,
          groups
        },
        current:
          state.adminEventRounds.current && state.adminEventRounds.current.id === round.id
            ? {
                ...round,
                groups
              }
            : state.adminEventRounds.current
      }
    };
  },

  clear: async (state: IStore, clubId: number, eventId: number, roundId: number) => {
    const response = await request.post(
      `${api.admin.events(clubId)}${eventId}/rounds/${roundId}/clear/`,
      {}
    );

    if (response.error) {
      return { errors: { adminEventRounds: { msg: 'Error reseting groups.' } } };
    }

    const round = state.adminEventRounds[roundId];
    const newGroups = (round as IEventDetailedRound).groups.map(group => ({ ...group, games: [] }));

    return {
      adminEventRounds: {
        ...state.adminEventRounds,
        [roundId]: {
          ...round,
          groups: newGroups
        }
      }
    };
  },

  setCurrent: async (state: IStore, round: IEventDetailedRound) => {
    if (state.adminEventRounds.current && round.id === state.adminEventRounds.current.id) {
      return state;
    }

    return {
      adminEventRounds: {
        ...state.adminEventRounds,
        current: round
      }
    };
  },

  autoFill: async (state: IStore, clubId: number, eventId: number, roundId: number) => {
    const response = await request.post(
      `${api.admin.events(clubId)}${eventId}/rounds/${roundId}/assign/`,
      {}
    );

    if (response.error) {
      return { errors: { adminEventRounds: { msg: 'Error reseting groups.' } } };
    }

    return {
      adminEventRounds: {
        ...state.adminEventRounds,
        [roundId]: response
      }
    };
  }
};

export default actions;
